import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { Form, FormGroup, SearchableItem, TypeAhead, ValidationMessage } from '~/components/Form';
import { usePatientService } from '~/services';
import { enumValue } from '~/util';
import { useRoleActions } from '~/util/useRoleActions';

const codeMaxLength = 20;
const descriptionMaxLength = 325;

interface AddCodeProps {
    patientId: number;
    setStatus: (page: string, status: number) => void;
    disabled?: boolean;
}
export function AddCode({ patientId, setStatus, disabled = false }: AddCodeProps) {
    const { addRiskGap, getICDCodes } = usePatientService();
    const canAdd = useRoleActions('allow_create_added_condition');
    const [show, setShow] = React.useState(false);
    const [code, setCode] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [description, setDescription] = React.useState('');
    const [displayPicker, setDisplayPicker] = React.useState(false);
    const menuTarget = React.useRef<HTMLDivElement>(null);

    function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
        setCode(e.target.value);
        setDisplayPicker(true);
    }

    function handleBlur(
        e: React.MouseEvent<HTMLElement, MouseEvent> | React.TouchEvent<HTMLElement>
    ) {
        if (!menuTarget.current?.contains(e.target as Node)) {
            setDisplayPicker(false);
        }
    }

    function handlePickerSelected(selected: SearchableItem) {
        setCode(selected.code);
        setDescription(selected.description);
        setDisplayPicker(false);
    }

    function handleExited() {
        setDescription('');
        setCode('');
    }

    async function handleSave() {
        setLoading(true);
        await addRiskGap(patientId, { code, description });
        setShow(false);
        setStatus('risk_gaps', enumValue(['gap', 'status', 'Pending']) as number);
        setLoading(false);
    }

    const codeLength = React.useMemo(() => {
        return code ? code.length : 0;
    }, [code]);

    const descriptionLength = React.useMemo(() => {
        return description ? description.length : 0;
    }, [description]);

    function handleICDSearch(value: string) {
        return getICDCodes(value);
    }

    return (
        <>
            <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setShow(true)}
                disabled={disabled}
            >
                <i className="fa-solid fa-plus" /> Add Condition
            </button>
            <BSModal
                className="addcode-modal"
                show={show}
                onHide={() => setShow(false)}
                onExited={handleExited}
                aria-labelledby="add-condition-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="add-condition-title" role="heading" aria-level={1}>
                        Add Condition
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body onMouseDown={handleBlur} onTouchStart={handleBlur}>
                    {!canAdd && (
                        <div className="permissions-alert">
                            You don’t have permission to add conditions on this record.
                        </div>
                    )}
                    <Form
                        id="add-code-form"
                        autoComplete="off"
                        onSubmit={handleSave}
                        values={{ code, description }}
                    >
                        <FormGroup htmlFor="code" className="mb-3">
                            <label className="form-label" htmlFor="code">
                                Code or Condition
                            </label>
                            <div className="typeahead-outer" ref={menuTarget}>
                                <input
                                    id="code"
                                    className="form-control"
                                    maxLength={codeMaxLength}
                                    required
                                    onChange={handleCodeChange}
                                    value={code}
                                    placeholder="Search or Fill Out"
                                    autoComplete="off"
                                />
                                <TypeAhead
                                    onSelect={handlePickerSelected}
                                    value={code}
                                    shouldDisplay={displayPicker}
                                    searchFunction={handleICDSearch}
                                />
                            </div>
                            <ValidationMessage type="required" className="pull-left">
                                Code or Condition is required.
                            </ValidationMessage>
                            <div>
                                <span className="pull-right">
                                    {codeLength} / {codeMaxLength}
                                </span>
                            </div>
                        </FormGroup>
                        <FormGroup htmlFor="description" className="mb-3 mt-4">
                            <label className="form-label" htmlFor="description">
                                Description
                            </label>
                            <textarea
                                id="description"
                                className="form-control"
                                rows={4}
                                maxLength={descriptionMaxLength}
                                onChange={(e) => setDescription(e.target.value)}
                                value={description}
                            />
                            <div>
                                <span className="pull-right">
                                    {descriptionLength} / {descriptionMaxLength}
                                </span>
                            </div>
                        </FormGroup>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => setShow(false)}
                    >
                        {canAdd ? 'Cancel' : 'Close'}
                    </button>
                    {canAdd && (
                        <button
                            className="btn btn-primary"
                            type="submit"
                            form="add-code-form"
                            disabled={loading || codeLength === 0}
                        >
                            {loading && (
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    className="me-2"
                                />
                            )}
                            Save
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
        </>
    );
}
